// London Smoke & Cure — shared product sale / pricing helpers (single source of truth).
//
// Consolidates the duplicated truthyOnSale / effective-price logic that previously lived
// in ProductItem, ProductCatalog, ProductDetail, Home, and unifiedProductService.
//
// Sale windows (additive, backwards compatible):
//   onSale + optional saleStartAt / saleEndAt (ISO strings or Date/ms).
//   Effective on sale = onSale flag truthy AND now within [start, end] (open-ended if missing).
//   Products with no window behave exactly like before (flag-only).

/** Truthy across legacy shapes: true, 1, 'x', 'yes', 'true', '1'. */
export function truthyFlag(val) {
    if (val === true) return true;
    if (typeof val === 'number') return val === 1;
    if (typeof val === 'string') {
        const s = val.trim().toLowerCase();
        return s === 'true' || s === '1' || s === 'yes' || s === 'x';
    }
    return false;
}

/** Featured flag tolerant of all stored shapes ('Featured This week' string/boolean, featured aliases). */
export function isFeatured(product) {
    if (!product || typeof product !== 'object') return false;
    return (
        truthyFlag(product['Featured This week']) ||
        truthyFlag(product.featured) ||
        truthyFlag(product.featuredThisWeek) ||
        truthyFlag(product.featured_this_week)
    );
}

/** Best seller flag tolerant of stored shapes. */
export function isBestSeller(product) {
    if (!product || typeof product !== 'object') return false;
    return (
        truthyFlag(product.bestSeller) ||
        truthyFlag(product.bestSellers) ||
        truthyFlag(product['Best Seller']) ||
        truthyFlag(product.best_seller)
    );
}

export function safeParseFloat(v) {
    if (v == null || v === '') return undefined;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : undefined;
}

/** Parse a sale-window boundary into epoch ms, or null when absent/invalid. */
export function parseSaleBoundaryMs(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    // Firestore Timestamp support
    if (typeof value === 'object' && typeof value.toDate === 'function') {
        const d = value.toDate();
        return Number.isNaN(d.getTime()) ? null : d.getTime();
    }
    const t = new Date(value).getTime();
    return Number.isNaN(t) ? null : t;
}

/**
 * Is the product on sale right now, honouring optional sale window?
 * @param {object} product
 * @param {number} [nowMs] — defaults to Date.now()
 */
export function isOnSaleNow(product, nowMs = Date.now()) {
    if (!product || typeof product !== 'object') return false;
    if (!truthyFlag(product.onSale)) return false;

    const salePrice = safeParseFloat(product.salePrice);
    if (salePrice == null) return false;

    const start = parseSaleBoundaryMs(product.saleStartAt);
    const end = parseSaleBoundaryMs(product.saleEndAt);
    if (start != null && nowMs < start) return false;
    if (end != null && nowMs > end) return false;
    return true;
}

/**
 * Effective customer price after honouring an active sale window.
 * Falls back to base Price when not on sale.
 * @returns {{ price: number, basePrice: number, onSale: boolean, previousPrice: number|undefined }}
 */
export function getEffectivePricing(product, nowMs = Date.now()) {
    const basePrice = safeParseFloat(product?.price ?? product?.Price) ?? 0;
    const onSale = isOnSaleNow(product, nowMs);
    const salePrice = safeParseFloat(product?.salePrice);
    const previousPrice = safeParseFloat(product?.previousPrice) ?? basePrice;
    return {
        price: onSale && salePrice != null ? salePrice : basePrice,
        basePrice,
        onSale,
        previousPrice,
    };
}

/** Human label describing a sale window, for admin UIs. */
export function describeSaleWindow(product) {
    const start = parseSaleBoundaryMs(product?.saleStartAt);
    const end = parseSaleBoundaryMs(product?.saleEndAt);
    if (start == null && end == null) return 'No window (always while on sale)';
    const fmt = (ms) =>
        new Date(ms).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    if (start != null && end != null) return `${fmt(start)} → ${fmt(end)}`;
    if (start != null) return `From ${fmt(start)}`;
    return `Until ${fmt(end)}`;
}
